import { useState } from 'react';
import {
  ClipboardText, CheckCircle, XCircle, CaretDown, CaretRight,
  Spinner, CloudArrowDown,
} from '@phosphor-icons/react';
import { useAssetStore } from '../../stores/assetStore';
import type { Asset, AssetVersion } from '@spine-workspace/shared';

interface ReviewQueueProps {
  onOpenAsset?: (asset: Asset) => void;
}

type FilterTab = 'pending' | 'all';

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function ReviewQueue({ onOpenAsset }: ReviewQueueProps) {
  const { submissions, reviewAsset, fetchSubmissions } = useAssetStore();
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState<FilterTab>('pending');
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});

  // submissions is now AssetVersion[] from the store
  const versions = submissions as unknown as AssetVersion[];
  const visible = tab === 'pending'
    ? versions.filter(v => v.submissionStatus === 'submitted')
    : versions;

  const pendingCount = versions.filter(v => v.submissionStatus === 'submitted').length;

  async function handleReview(v: AssetVersion, status: 'approved' | 'rejected') {
    setReviewing(v.id);
    try {
      // reviewAsset still works — we pass version id as first arg
      // the updated store calls the /versions/:id/review endpoint
      await reviewAsset(v.id, notesDraft[v.id] || undefined, status);
    } finally {
      setReviewing(null);
      setNotesDraft((d) => { const n = { ...d }; delete n[v.id]; return n; });
    }
  }

  // Build a fake Asset-like object from a version for the canvas to load
  function versionToAsset(v: AssetVersion): Asset {
    return {
      id: v.assetId,
      ownerId: '',
      folderId: null,
      name: v.assetName ?? 'Unknown',
      slug: '',
      spineVersion: v.spineVersion,
      jsonR2Key: v.jsonR2Key,
      atlasR2Key: v.atlasR2Key,
      pngR2Keys: v.pngR2Keys,
      metadata: v.metadata,
      isShared: false,
      fileSize: v.fileSize,
      createdAt: v.createdAt,
      updatedAt: v.createdAt,
      jsonUrl: v.jsonUrl,
      atlasUrl: v.atlasUrl,
      pngUrls: v.pngUrls,
    };
  }

  if (versions.length === 0) return null;

  return (
    <div className="border-b border-border-subtle">
      {/* Header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 px-3 py-2 hover:bg-bg-hover transition-colors"
      >
        {open
          ? <CaretDown size={9} weight="bold" className="text-text-tertiary shrink-0" />
          : <CaretRight size={9} weight="bold" className="text-text-tertiary shrink-0" />
        }
        <ClipboardText size={11} className="text-amber-400 shrink-0" />
        <span className="text-[10px] font-mono font-semibold text-text-secondary uppercase tracking-wide flex-1 text-left">
          Review Queue
        </span>
        {pendingCount > 0 && (
          <span className="text-[9px] font-mono bg-amber-400/20 text-amber-400 px-1.5 py-0.5 rounded-full">
            {pendingCount}
          </span>
        )}
      </button>

      {open && (
        <div className="pb-1">
          {/* Filter tabs */}
          <div className="flex border-b border-border-subtle mx-2 mb-1">
            {(['pending', 'all'] as FilterTab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wide transition-colors ${
                  tab === t ? 'text-accent border-b border-accent' : 'text-text-tertiary hover:text-text-secondary'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {visible.length === 0 ? (
            <p className="text-[9px] text-text-tertiary text-center py-3">
              {tab === 'pending' ? 'No pending submissions' : 'No submissions'}
            </p>
          ) : (
            visible.map((v) => (
              <div key={v.id} className="mx-2 mb-1 rounded-md bg-bg-input border border-border overflow-hidden">
                {/* Row header */}
                <div className="flex items-center gap-2 px-2 py-1.5">
                  {/* Version badge */}
                  <span className="shrink-0 text-[8px] font-mono font-bold bg-accent/20 text-accent px-1.5 py-0.5 rounded">
                    v{v.versionNumber}
                  </span>

                  {/* Asset name */}
                  <span className="flex-1 text-[10px] font-sans text-text-primary truncate" title={v.assetName}>
                    {v.assetName}
                  </span>

                  {/* Status dot */}
                  <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${
                    v.submissionStatus === 'submitted' ? 'bg-amber-400' :
                    v.submissionStatus === 'approved'  ? 'bg-emerald-400' : 'bg-red-400'
                  }`} />

                  {/* Open in canvas */}
                  {onOpenAsset && v.submissionStatus === 'submitted' && (
                    <button
                      onClick={() => onOpenAsset(versionToAsset(v))}
                      className="shrink-0 p-0.5 rounded text-text-tertiary hover:text-accent hover:bg-bg-hover transition-colors"
                      title="Open in canvas"
                    >
                      <CloudArrowDown size={11} />
                    </button>
                  )}

                  {/* Expand toggle */}
                  {v.submissionStatus === 'submitted' && (
                    <button
                      onClick={() => setExpanded((x) => x === v.id ? null : v.id)}
                      className="shrink-0 p-0.5 rounded text-text-tertiary hover:text-text-secondary transition-colors"
                    >
                      {expanded === v.id
                        ? <CaretDown size={9} weight="bold" />
                        : <CaretRight size={9} weight="bold" />
                      }
                    </button>
                  )}
                </div>

                {/* Submitter + time */}
                <div className="px-2 pb-1 flex items-center gap-1 text-[9px] text-text-tertiary font-mono">
                  <span className="truncate">{v.submitterName}</span>
                  <span>·</span>
                  <span className="shrink-0">{timeAgo(v.submittedAt)}</span>
                  {v.driveFolderName && (
                    <>
                      <span>·</span>
                      <span className="text-blue-400 truncate" title={v.driveFolderName}>Drive</span>
                    </>
                  )}
                </div>

                {/* Expanded review panel */}
                {expanded === v.id && (
                  <div className="px-2 pb-2 space-y-1.5 border-t border-border-subtle pt-1.5">
                    <textarea
                      placeholder="Optional review notes…"
                      rows={2}
                      value={notesDraft[v.id] ?? ''}
                      onChange={(e) => setNotesDraft((d) => ({ ...d, [v.id]: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full bg-bg-surface border border-border rounded px-2 py-1 text-[10px] font-sans text-text-primary placeholder:text-text-tertiary resize-none focus:outline-none focus:border-accent transition-colors"
                    />
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleReview(v, 'approved')}
                        disabled={reviewing === v.id}
                        data-testid={`review-approve-${v.id}`}
                        className="flex-1 flex items-center justify-center gap-1 py-1 rounded text-[9px] font-sans bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 disabled:opacity-50 transition-colors"
                      >
                        {reviewing === v.id
                          ? <Spinner size={9} className="animate-spin" />
                          : <CheckCircle size={10} weight="fill" />
                        }
                        Approve
                      </button>
                      <button
                        onClick={() => handleReview(v, 'rejected')}
                        disabled={reviewing === v.id}
                        data-testid={`review-reject-${v.id}`}
                        className="flex-1 flex items-center justify-center gap-1 py-1 rounded text-[9px] font-sans bg-red-500/15 text-red-400 hover:bg-red-500/25 disabled:opacity-50 transition-colors"
                      >
                        {reviewing === v.id
                          ? <Spinner size={9} className="animate-spin" />
                          : <XCircle size={10} weight="fill" />
                        }
                        Request Changes
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}

          <button
            onClick={() => fetchSubmissions()}
            className="w-full text-[9px] font-mono text-text-tertiary hover:text-text-primary py-1 transition-colors"
          >
            Refresh
          </button>
        </div>
      )}
    </div>
  );
}
